// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/pages/DimensionPage.tsx (ADAPT)
// ADAPTED: reads our view model. Not ported: `OwnerLine` (the Omnichannel "Assign a person" form kept its name in
// component state only; there is nowhere to store an owner).
/**
 * Growth Score → one dimension: its score against the cohort, every check as
 * an opportunity (worst first), how we measured it, and the other dimensions
 * to jump to. One template for all six; Omnichannel, which never scores,
 * gets an owner instead.
 */
import { ArrowRight } from 'lucide-react'
import { SCORE_REFERENCE } from '@/contracts/onboarding-session'
import { Button } from '@/components/ui/shadcn/button'
import type { DimensionKey } from '../growth-score.types'
import { GrowthPage, GrowthSection, useGrowthNav } from '../GrowthPage'
import { useGrowthScore } from '../GrowthScoreContext'
import { MeasuredBody } from '../components/MeasuredBody'
import { OpportunityList } from '../components/OpportunityList'
import { SearchBlocks } from '../components/SearchBlocks'
import { DimensionDot, DimensionIcon, ScoreBar, ScoreFigure } from '../components/visuals'

export function DimensionPage({ dim }: { dim: DimensionKey }) {
  const vm = useGrowthScore()
  const nav = useGrowthNav()
  const d = vm.dimensions.find((x) => x.key === dim)!
  const others = vm.dimensions.filter((x) => x.key !== dim)

  return (
    <GrowthPage
      title={d.name}
      back
      hero={{
        icon: <DimensionIcon dimension={d.key} />,
        subtitle: d.weight ? `${d.weight}% of the score · ${SCORE_REFERENCE} = cohort median` : 'A signal, not part of the score',
        aside:
          d.score != null ? (
            <div className="flex w-36 flex-col items-end gap-1.5">
              <ScoreFigure value={d.score} size="lg" />
              <ScoreBar value={d.score} />
            </div>
          ) : undefined,
        children: (
          <>
            <p className="text-body-sm text-ds-text-2">
              <span className="text-tab text-foreground">{d.gap.lead}</span> {d.gap.rest}
            </p>
          </>
        ),
      }}
    >

      <GrowthSection title="Where you have the most to gain">
        <OpportunityList dimension={d} />
      </GrowthSection>

      {d.key === 'search' ? (
        <GrowthSection
          title="How we measured it"
          actions={
            <Button type="button" variant="outline" onClick={() => nav.open({ kind: 'search' })}>
              Open the Search &amp; AI assessment
              <ArrowRight aria-hidden />
            </Button>
          }
        >
          <SearchBlocks />
        </GrowthSection>
      ) : (
        <GrowthSection title="How we measured it">
          <MeasuredBody dimension={d.key} />
        </GrowthSection>
      )}

      <nav aria-label="Other dimensions" className="flex flex-wrap gap-2">
        {others.map((x) => (
          <Button key={x.key} type="button" variant="outline" size="xs" onClick={() => nav.open({ kind: 'dimension', dim: x.key })}>
            <DimensionDot dimension={x.key} />
            {x.name}
          </Button>
        ))}
      </nav>
    </GrowthPage>
  )
}
